import {
    Flex,
    Input,
    useStyles,
    useAppDisplayModeContext,
    SearchBar as AriaSearchBar,
    pxArrayToRem,
    pxToRem,
    useTheme,
} from '@archipro-design/aria';
import {
    SearchBarSection as SearchBarSectionDesktop,
    SearchBarInput,
} from './ContactPage.desktop.style';
import { SearchBarSection as SearchBarSectionMobile } from './ContactPage.mobile.style';

import { CloseLine, SearchLine } from '@archipro-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'ahooks';

interface SearchBarProps {
    hideInput?: boolean;
    onSearchChange: (keyword: string) => void;
    onSearchClick: (keyword: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
    hideInput,
    onSearchChange,
    onSearchClick,
}) => {
    const [keyword, setKeyword] = useState('');
    const { mobile: isMobile } = useAppDisplayModeContext();

    const { css } = useStyles({ isEmptyKeyword: keyword === '' });
    const { siteVariables } = useTheme();

    const debouncedKeyword = useDebounce(keyword, { wait: 600 });
    const previousKeywordRef = useRef(debouncedKeyword);

    useEffect(() => {
        if (previousKeywordRef.current !== debouncedKeyword) {
            onSearchChange(debouncedKeyword);
            previousKeywordRef.current = debouncedKeyword;
        }
    }, [debouncedKeyword, onSearchChange]);

    const onChangeKeyword = (value: string) => {
        setKeyword(value);
    };

    const onClose = () => {
        setKeyword('');
    };
    return isMobile ? (
        <>
            {!hideInput && (
                <div className={css(SearchBarSectionMobile)}>
                    <AriaSearchBar
                        iconButtonAfter={{
                            onClick: onClose,
                            size: 24,
                            padding: 0,
                        }}
                        iconButtonBefore={{
                            icon: <SearchLine />,
                            size: 24,
                        }}
                        searchInput={{
                            input: {
                                value: keyword,
                            },
                            onChange: (e) =>
                                onChangeKeyword(
                                    (e.target as HTMLInputElement).value
                                ),
                            onClick: (e) =>
                                onSearchClick(
                                    (e.target as HTMLInputElement).value
                                ),
                        }}
                    />
                </div>
            )}
        </>
    ) : (
        <Flex className={css(SearchBarSectionDesktop)}>
            {!hideInput && (
                <Flex hAlign="center">
                    <Input
                        fluid
                        placeholder={'Search'}
                        className={css(SearchBarInput)}
                        onChange={(e) => {
                            onChangeKeyword(
                                (e.target as HTMLInputElement).value
                            );
                        }}
                        onClick={(e) => {
                            onSearchClick((e.target as HTMLInputElement).value);
                        }}
                        value={keyword}
                        icon={
                            keyword.length > 0 ? (
                                <CloseLine
                                    size="1x"
                                    onClick={onClose}
                                    variables={{
                                        '1xSize': pxToRem(20),
                                    }}
                                />
                            ) : (
                                <SearchLine
                                    size="1x"
                                    onClick={() => {
                                        onSearchClick(debouncedKeyword);
                                    }}
                                    variables={{
                                        '1xSize': pxToRem(20),
                                    }}
                                />
                            )
                        }
                        iconPosition={'start'}
                        variables={{
                            inputPaddingWithIconAtStart: pxArrayToRem([
                                0, 0, 0, 29.15,
                            ]),
                            iconLeft: pxToRem(0),
                            placeholderColor:
                                siteVariables.colorScheme.default?.foreground,
                        }}
                    />
                </Flex>
            )}
        </Flex>
    );
};
